import { useEffect, useState } from "react";
import { Button, Col, FormCheck, FormControl, FormLabel, FormSelect, Row, Spinner } from "react-bootstrap";
import PrivateApi from "../../../services/PrivateApiService";
import Validator from "../../../services/Validator";
import { useQueryClient } from "react-query";

/**
 * InvoiceView component
 * 
 * This component is used to display the invoice view settings for the use in the settings page.
 * 
 * @param {object} user - user object
 * @returns html fields
 */
const InvoiceView = ({user}) => {

    const API = new PrivateApi(); // API CALL
    const queryClient = useQueryClient(); // query client for invalidating queries
    const VALIDATOR = new Validator(); // VALIDATOR
    const [view, setView] = useState("default"); // view state
    const [ApiError] = useState("Fehler beim Speichern der Änderungen. Bitte versuchen Sie später nochmal."); // api error state
    const [errors, setErrors] = useState({}); // errors state

    // fields 
    const [documentPreference, setDocumentPreference] = useState(user.x_document_preference === "False"? "Kundenportal": user.x_document_preference); // invoice receive state
    const [preferenceEmail1, setPreferenceEmail1] = useState(user.x_document_preference_email === "False"? "" : user.x_document_preference_email); // email state
    const [preferenceEmail2, setPreferenceEmail2] = useState(''); // email state
    const [preferenceDeckblatt, setPreferenceDeckblatt] = useState(user.x_document_preference_deckblatt); // deckblatt state
    
    // bool states for hidding elements
    const [saveBtnHidden, setSaveBtnHidden] = useState(true); // save button hidden state
    const [emailFieldHidden, setEmailFieldHidden] = useState(true); // email field hidden state
    const [isLoading, setIsLoading] = useState(false); // loading state

    // efect for showing hidding fields
    useEffect(() => {
       if (documentPreference !== user.x_document_preference) {
            setSaveBtnHidden(false); // show save button
        } else if (preferenceDeckblatt !== user.x_document_preference_deckblatt) {
            setSaveBtnHidden(false); // show save button
        } else if (preferenceEmail1 !== user.x_document_preference_email && preferenceEmail1 === preferenceEmail2) {
            setSaveBtnHidden(false); // show save button
        } else {
            setSaveBtnHidden(true); // hide save button 
        } 

    }, [documentPreference, preferenceDeckblatt, preferenceEmail1, preferenceEmail2, user.x_document_preference, user.x_document_preference_deckblatt])

    // save handler
    const saveHandler = () => {
        let data = {}; // data object for api call
        
        // * check and fill data object
        if (documentPreference !== user.x_document_preference) {
            data.x_document_preference = documentPreference;
        }
        // in case of email, check if email is valid and if both emails are the same
        if (documentPreference === "Email" && preferenceEmail1 !== user.x_document_preference_email) {
            if (!VALIDATOR.EmailValid(preferenceEmail1)) return setErrors({preferenceEmail1: "E-Mail-Adresse ist ungültig"});
            if (!VALIDATOR.EmailValid(preferenceEmail2)) return setErrors({preferenceEmail2: "E-Mail-Adresse ist ungültig"});
            if (preferenceEmail1 !== preferenceEmail2) return setErrors({preferenceEmail2: "E-Mail-Adressen stimmen nicht überein"});

                data.x_document_preference_email = preferenceEmail1; // set email
            }
            data.x_document_preference = documentPreference; // set document preference

        // check if deckblatt is changed
        if (preferenceDeckblatt !== user.x_document_preference_deckblatt) {
            data.x_document_preference_deckblatt = preferenceDeckblatt; // set deckblatt
        }

        setIsLoading(true); // set loading state

        // call api to save data
        API.post_Partner_document_preference_save(data)
            .then((resp) => {
                setView("saved");
                setIsLoading(false); // set loading state
                setSaveBtnHidden(true); // hide save button
                setEmailFieldHidden(true); // hide email fields
                queryClient.invalidateQueries("GET_PROFILE"); // invalidate query to get updated data
            })
            .catch((error) => {
                setView("error");
                setIsLoading(false); // set loading state
                setSaveBtnHidden(true); // hide save button
                setEmailFieldHidden(true); // hide email fields
            });
    }

    return (
        <>
            <Row>
                <Col xl="12">
                    <h5>Dokumentenzustellung</h5>
                    <p className="text-muted">Hier können Sie auswählen, wie Sie Ihre Dokumente erhalten</p>
                </Col>
                <hr />
            </Row>
            {view === "default" &&
                <>
                    <Row>
                        <Col xl="5">
                            <FormLabel>Dokumentenzustellung per</FormLabel>
                            <FormSelect value={documentPreference} onChange={(e) => setDocumentPreference(e.target.value)}>
                                <option value="Email">E-Mail</option>
                                <option value="Kundenportal">Kundenportal</option>
                            </FormSelect>
                        </Col>
                    </Row>
                    <Row hidden={documentPreference !== "Email"} className="mt-3">
                        <Col xl="6">
                            <FormLabel>E-Mail-Adresse</FormLabel>
                            <FormControl
                                type="email"
                                placeholder="E-Mail-Adresse"
                                value={preferenceEmail1}
                                onChange={(e) => setPreferenceEmail1(e.target.value)}
                                isValid={preferenceEmail1.length > 0 && preferenceEmail1.includes("@") && preferenceEmail1.includes(".")}
                                isInvalid={errors.preferenceEmail1}
                            />
                        </Col>
                        <Col xl="6">
                            <FormLabel>E-Mail-Adresse bestätigen</FormLabel>
                            <FormControl
                                type="email"
                                placeholder="E-Mail-Adresse bestätigen"
                                value={preferenceEmail2}
                                onChange={(e) => setPreferenceEmail2(e.target.value)}
                                isValid={preferenceEmail2.length > 0 && preferenceEmail2.includes("@") && preferenceEmail2.includes(".") && preferenceEmail2 === preferenceEmail1}
                                isInvalid={errors.preferenceEmail2}
                            />
                        </Col>

                    </Row>
                    {/* user.x_is_house_management === true &&
                        <Row className="mt-4">
                            <Col xl="12">
                                <FormCheck
                                    type="checkbox"
                                    label="Ich möchte Deckblätter für meine Rechnungen erhalten"
                                    id="houseManagement"
                                    defaultChecked={user.x_document_preference_deckblatt}
                                    value={user.x_document_preference_deckblatt}
                                    onChange={(e) => setPreferenceDeckblatt(e.target.checked)}
                                />
                            </Col>
                        </Row> */}
                    
                    <Row className="mt-4" hidden={saveBtnHidden}>
                        <Col className="text-end">
                            {isLoading?
                                <Spinner animation="border" className="text-energo" />
                                :
                                <Button className="btn-energo" onClick={saveHandler}>Änderungen speichern</Button>
                            }
                        </Col>
                    </Row>
                </>
            }
            {view === "saved" &&
                <>
                    <Row>
                        <Col xl="12">
                            <p className="text-energo">Änderungen erfolgreich gespeichert!</p>
                        </Col>
                    </Row>
                </>
            }
            {view === "error" &&
                <>
                    <Row>
                        <Col xl="12">
                            <p className="text-danger">{ApiError}</p>
                        </Col>
                    </Row>
                </>
            }
        </>
    );
}
export default InvoiceView;